import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { AlertCircle, Building2, CreditCard, Loader2 } from 'lucide-react';
import { useTheme } from '@/hooks/use-theme';
import { Button } from '@/components/atoms/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import {
  defaultPayabliRail,
  payabliRefusalMessage,
  payabliRefusalSummary,
  type PayabliComponentSuccess,
  type PayabliMode,
  type PayabliRail,
} from '@/lib/payments/payabli';
import type { PaymentMethodType } from '@/types/contact';
import { CustomerPayButton, PaymentRailSwitch } from './PaymentRailSwitch';
import { usePayabliEmbed } from './usePayabliEmbed';

/**
 * The tokenized method the Payabli component hands back, normalized into the
 * fields our `tokenize` endpoint needs. `temporaryToken` is the short-lived
 * token the backend exchanges for a permanent stored method; the card metadata
 * comes from the component's success payload (nesting varies by version — see
 * `extractResult` — so we read defensively).
 */
/**
 * Payor identity forwarded to the embedded component. Paypoints configured to
 * require a customer identifier reject tokenization client-side (an opaque
 * `customerError` from the component) when none is supplied, so every mount
 * must pass at least `customerNumber` — which MUST be the Notifi contact id,
 * because the backend exchanges the temp token binding
 * `customerData.customerNumber = contactId` and Payabli rejects a mismatched
 * payor on charge.
 */
export interface PayabliCustomerData {
  customerNumber: string;
  firstName?: string;
  lastName?: string;
  billingEmail?: string;
}

export interface PayabliSetupResult {
  temporaryToken: string;
  type: PaymentMethodType;
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
}

/**
 * How long we wait for `payabliExec('method')` to call back before handing the
 * form back to the payer. The bundle answers in well under a second; when it
 * answers not at all (a killed iframe, a dropped connection mid-tokenization)
 * nothing else ever clears `isSubmitting`, and a spinner on a dead Pay button
 * — with the rail switch disabled behind it — was the end of that payment.
 */
const SUBMIT_TIMEOUT_MS = 30_000;

/**
 * How the form looks. `default` is the compact form staff dialogs and pay pages
 * use. `customer` is the payment-method page design: sentence-case labels
 * above the fields, our stylesheet inside Payabli's iframe (`public/brand/payabli/`),
 * a segmented Card | Bank switch and a large submit button.
 */
export type PayabliFormAppearance = 'default' | 'customer';

type PayabliInputs = Record<string, Record<string, unknown>>;

/**
 * Field layout per rail. Explicit descriptors so the card form ALWAYS renders
 * (and requires) the CVV, regardless of paypoint-level embedded-component
 * defaults — a card accepted without its security code is a
 * downgraded-authentication charge the tenant eats disputes for. The bank rail
 * gets descriptors too: once ANY per-method layout is supplied the component
 * only renders fields it has a descriptor for, so `ach: { enabled: true }` with
 * no `inputs` could hand the customer an empty bank tab.
 */
function payabliInputs(
  appearance: PayabliFormAppearance,
  holderName: string | undefined,
): { card: PayabliInputs; ach: PayabliInputs } {
  if (appearance === 'default') {
    return {
      card: {
        cardHolderName: { label: 'NAME ON CARD', size: 12, row: 0, order: 0 },
        cardNumber: { label: 'CARD NUMBER', size: 6, row: 1, order: 0 },
        cardExpirationDate: { label: 'EXPIRATION', size: 6, row: 1, order: 1 },
        cardCvv: { label: 'CVV', size: 6, row: 2, order: 0 },
        cardZipcode: { label: 'ZIP CODE', size: 6, row: 2, order: 1 },
      },
      ach: {
        achAccountHolderName: { label: 'NAME ON ACCOUNT', size: 12, row: 0, order: 0 },
        achAccountType: { label: 'ACCOUNT TYPE', size: 6, row: 1, order: 0 },
        achRouting: { label: 'ROUTING NUMBER', size: 6, row: 1, order: 1 },
        achAccount: { label: 'ACCOUNT NUMBER', size: 12, row: 2, order: 0 },
      },
    };
  }
  // Labels sit above the field (`floating: false`). The component lays out
  // rows 0-2 only; a fourth row lands inside the third, so the ZIP code is the
  // last, full-width entry of row 2 and wraps below expiry and security code.
  const holder = holderName?.trim() ? { value: holderName.trim() } : {};
  return {
    card: {
      cardHolderName: {
        label: 'Name on card',
        placeholder: 'Full name',
        floating: false,
        size: 12,
        row: 0,
        order: 0,
        ...holder,
      },
      cardNumber: {
        label: 'Card number',
        placeholder: '1234 1234 1234 1234',
        floating: false,
        size: 12,
        row: 1,
        order: 0,
      },
      cardExpirationDate: {
        label: 'Expiry',
        placeholder: 'MM / YY',
        floating: false,
        size: 6,
        row: 2,
        order: 0,
      },
      cardCvv: {
        label: 'Security code',
        placeholder: 'CVC',
        floating: false,
        size: 6,
        row: 2,
        order: 1,
      },
      cardZipcode: {
        label: 'Billing ZIP / postal code',
        placeholder: 'ZIP code',
        floating: false,
        size: 12,
        row: 2,
        order: 2,
      },
    },
    ach: {
      achAccountHolderName: {
        label: 'Name on account',
        placeholder: 'Full name',
        floating: false,
        size: 12,
        row: 0,
        order: 0,
        ...holder,
      },
      achAccountType: { label: 'Account type', floating: false, size: 12, row: 1, order: 0 },
      achRouting: {
        label: 'Routing number',
        placeholder: '9 digits',
        floating: false,
        size: 6,
        row: 2,
        order: 0,
      },
      achAccount: {
        label: 'Account number',
        placeholder: 'Account number',
        floating: false,
        size: 6,
        row: 2,
        order: 1,
      },
    },
  };
}

function toStr(value: unknown): string | undefined {
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function extractResult(r: PayabliComponentSuccess, rail: PayabliRail): PayabliSetupResult | null {
  const data = (r.responseData ?? {}) as Record<string, unknown>;
  // In temporaryToken:true mode the temp token comes back as referenceId.
  const temporaryToken =
    toStr(r.referenceId) ??
    toStr(r.methodReferenceId) ??
    toStr(data.referenceId) ??
    toStr(data.methodReferenceId);
  if (!temporaryToken) return null;

  const last4 =
    toStr(data.maskedAccount)?.replace(/\D/g, '').slice(-4) ?? toStr(data.last4) ?? null;

  // Card expiry may arrive as "MMYY", "MM/YY", or "MM/YYYY".
  const expRaw = toStr(data.expDate) ?? toStr(data.cardexp);
  let expMonth: number | null = null;
  let expYear: number | null = null;
  if (expRaw) {
    const digits = expRaw.replace(/\D/g, '');
    if (digits.length >= 4) {
      expMonth = Number(digits.slice(0, 2)) || null;
      const yy = digits.slice(-2);
      expYear = yy ? 2000 + Number(yy) : null;
    }
  }

  // The rail the payer actually filled in is the authority here. The payload's
  // `method` is honoured when it says something we recognise, but it is absent
  // on some bundle versions — and defaulting THAT to a card stored a bank
  // account as a credit card: mislabelled everywhere it is listed, and eligible
  // for the card service fee on every later charge.
  const rawMethod = (toStr(data.method) ?? toStr(r.method) ?? '').toLowerCase();
  const type: PaymentMethodType = /ach|bank|echeck/.test(rawMethod)
    ? 'ACH'
    : rawMethod.includes('card')
      ? 'CREDIT_CARD'
      : rail === 'ach'
        ? 'ACH'
        : 'CREDIT_CARD';
  const isAch = type === 'ACH';

  return {
    temporaryToken,
    type,
    // A bank account has no card brand and no expiry; carrying either would
    // render "Visa •••• 1234 · 12/2030" over a checking account.
    brand: isAch
      ? (toStr(data.bankName) ?? null)
      : (toStr(data.binCardBrand) ?? toStr(data.brand) ?? toStr(data.cardType) ?? null),
    last4,
    expMonth: isAch ? null : expMonth,
    expYear: isAch ? null : expYear,
  };
}

/**
 * Stands in for the Payabli fields while the iframe loads, in the page's own
 * colours and at roughly the fields' height, so the page neither flashes
 * Payabli's white loading screen nor jumps when the form appears.
 */
function PayabliFieldsPlaceholder({ rail }: { rail: PayabliRail }) {
  // Field widths per row, as the customer layout draws them.
  const rows = rail === 'ach' ? [[1], [1], [0.5, 0.5]] : [[1], [1], [0.5, 0.5], [1]];
  return (
    <div
      role="status"
      aria-label="Loading the secure payment form"
      className="space-y-[18px] p-1 pb-[22px]"
      data-testid="payabli-fields-placeholder"
    >
      {rows.map((row, index) => (
        <div key={index} className="flex gap-4">
          {row.map((width, cell) => (
            <div key={cell} className="space-y-2" style={{ flex: width }}>
              <div className="h-3.5 w-24 animate-pulse rounded bg-stone-200 dark:bg-[#2a2826]" />
              <div className="h-12 animate-pulse rounded-[10px] border border-stone-200 bg-white dark:border-[#3a3835] dark:bg-[#161513]" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Payabli analog of `StripePaymentMethodSetupForm`. Mounts the embedded
 * component with the tenant's public token + entryPoint in temporary-token
 * mode (raw PAN never reaches our backend) and, on success, hands the
 * normalized temp token + card metadata back to the caller via `onComplete`
 * — which posts it to the `tokenize` endpoint to mint the permanent method.
 *
 * The component renders ONE rail per instance (`defaultOpen`) and ships no tab
 * of its own, so when the tenant accepts both we render the Card | Bank switch
 * ourselves and re-create the component on the other rail. Without it every
 * payer was funnelled onto the card rail — under a page promising "bank
 * transfers have no fee" — because the bank rail had no door.
 */
export function PayabliPaymentMethodSetupForm({
  publicToken,
  entryPoint,
  mode,
  onComplete,
  submitLabel = 'Save card',
  methods,
  customerData,
  onRailChange,
  achSavings,
  errorTitle = 'Unable to save payment method',
  disabled,
  appearance = 'default',
  holderName,
  beforeSubmit,
}: {
  publicToken: string;
  entryPoint: string;
  mode: PayabliMode;
  onComplete: (result: PayabliSetupResult) => Promise<void>;
  /** A function names what the payer is saving on the rail they are on. */
  submitLabel?: string | ((rail: PayabliRail) => string);
  /** Optional invoice savings shown inside the bank-account choice. */
  achSavings?: ReactNode;
  /** Rails to offer. Defaults to both enabled when omitted. */
  methods?: { card?: boolean; ach?: boolean };
  /** Payor identity — required by paypoints that demand a customer identifier. */
  customerData?: PayabliCustomerData;
  /**
   * Fired when the payer switches rails. Hosting pages MUST follow it: a card
   * service fee may only be shown — and only attested to the charge endpoint —
   * for a payer who is actually on the card rail.
   */
  onRailChange?: (rail: PayabliRail) => void;
  /** Alert heading for a tokenization failure ("Payment failed" on pay pages). */
  errorTitle?: string;
  /**
   * Blocks submission without tearing down the mounted iframe — used by
   * callers that gate on something outside the component's own view (a stale
   * server fee quote, a missing ACH authorization). Never toggled internally.
   */
  disabled?: boolean;
  /** `customer` for the payment-method pages a customer opens from a link. */
  appearance?: PayabliFormAppearance;
  /**
   * Prefills the name on the card / account (`customer` appearance only). The
   * payer can still change it: the card may be in someone else's name.
   */
  holderName?: string;
  /** Rendered between the fields and the submit button, e.g. a consent to sign. */
  beforeSubmit?: ReactNode;
}) {
  const cardEnabled = methods?.card ?? true;
  const achEnabled = methods?.ach ?? true;
  const bothRails = cardEnabled && achEnabled;
  const [selectedRail, setSelectedRail] = useState<PayabliRail>(() =>
    defaultPayabliRail({ card: cardEnabled, ach: achEnabled }),
  );
  // A tenant that turns a rail off mid-session must never leave the form
  // pointing at a rail the charge path would reject.
  const rail: PayabliRail =
    (selectedRail === 'card' && !cardEnabled) || (selectedRail === 'ach' && !achEnabled)
      ? defaultPayabliRail({ card: cardEnabled, ach: achEnabled })
      : selectedRail;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // The component callbacks are built per mount (see `usePayabliEmbed`), so
  // they read the caller's callbacks through refs rather than closing over one
  // render's copies.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onRailChangeRef = useRef(onRailChange);
  onRailChangeRef.current = onRailChange;
  const submitTimeoutRef = useRef<number | null>(null);
  /**
   * The mount whose `payabliExec('method')` we are still willing to honour.
   *
   * The bundle's callbacks are fire-and-forget: it will happily call back after
   * we gave up on the submit, and after a rail switch or a Retry has thrown the
   * instance away. Both cases hand us a token the payer no longer expects to be
   * charged — a stale CARD token after they moved to the bank rail would be
   * charged with the wrong fee attestation, or charged twice. So a token is
   * only accepted while it belongs to the live instance AND to a submit that
   * has not already been resolved or abandoned.
   */
  const activeSubmitRef = useRef<object | null>(null);

  /** Leave the submitting state, and never with a tokenization timer armed. */
  const finishSubmitting = useCallback(() => {
    if (submitTimeoutRef.current !== null) {
      window.clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
    activeSubmitRef.current = null;
    setIsSubmitting(false);
  }, []);

  // The hosting page's fee note, button total and `surchargeDisclosed`
  // attestation all key off the rail, so it must never learn about a change
  // late. `selectRail` reports the payer's own clicks; this reports the
  // corrections above — a tenant disabling the card rail mid-session would
  // otherwise leave the page attesting a card fee over a bank charge.
  useEffect(() => {
    onRailChangeRef.current?.(rail);
  }, [rail]);

  // Remount only when the customerData contents change, not its identity.
  const customerDataKey = customerData ? JSON.stringify(customerData) : null;
  const isCustomer = appearance === 'customer';
  // Payabli's iframe cannot see the page's theme, so it gets the stylesheet for
  // the palette the page is drawn in, and remounts when that changes. It lives
  // under /brand/ because the CDN only sends listed paths to the static bucket
  // (STATIC_S3_PATTERNS in infra/lib/frontend-stack.ts); anything else goes to
  // the API and comes back as a 404 page, leaving Payabli's form unstyled.
  const { forcedTheme, resolvedTheme } = useTheme();
  const customCssUrl = isCustomer
    ? `${window.location.origin}/brand/payabli/form-${
        (forcedTheme ?? resolvedTheme) === 'dark' ? 'dark' : 'light'
      }.css`
    : null;

  const embed = usePayabliEmbed({
    mode,
    remountKey: JSON.stringify([
      publicToken,
      entryPoint,
      cardEnabled,
      achEnabled,
      customerDataKey,
      rail,
      appearance,
      holderName ?? null,
      customCssUrl,
    ]),
    // A remount starts over: a stale tokenization error from the rail the
    // payer just left, and — the one that bricks the page — a stale
    // `isSubmitting`, which would leave Retry handing the payer back a form
    // whose Pay button is disabled forever.
    onMount: () => {
      setErrorMessage(null);
      finishSubmitting();
    },
    onUnmount: (token) => {
      if (activeSubmitRef.current === token) activeSubmitRef.current = null;
      if (submitTimeoutRef.current !== null) {
        window.clearTimeout(submitTimeoutRef.current);
        submitTimeoutRef.current = null;
      }
    },
    buildConfig: ({ token, isCurrent, markRendered }) => {
      /** This instance still owns the submit the callback is answering. */
      const ownsActiveSubmit = () => isCurrent() && activeSubmitRef.current === token;
      const payor = customerDataKey
        ? (JSON.parse(customerDataKey) as PayabliCustomerData)
        : undefined;
      const inputs = payabliInputs(appearance, holderName);
      return {
        type: 'methodEmbedded',
        token: publicToken,
        entryPoint,
        defaultOpen: rail,
        // Temporary token: the component returns a short-lived token; the
        // backend converts it to a permanent, contact-bound stored method.
        temporaryToken: true,
        ...(customCssUrl ? { customCssUrl } : {}),
        card: { enabled: cardEnabled, inputs: inputs.card },
        ach: { enabled: achEnabled, inputs: inputs.ach },
        ...(payor ? { customerData: { ...payor } } : {}),
        functionCallBackReady: () => {
          // Per-field validation, not a mount event — but if it fires the
          // form is unquestionably rendered, so honour it as a backstop for
          // a bundle that stops posting mount messages.
          markRendered();
        },
        functionCallBackSuccess: (result) => {
          // Late or orphaned: the submit timed out, or a rail switch / Retry
          // replaced this instance. Charging this token now would bill a
          // payer who was told the form did not respond, or bill the card
          // they abandoned for the bank rail.
          if (!ownsActiveSubmit()) return;
          // One terminal outcome per submit. The tokenization deadline is
          // met the moment the bundle answers — the wait on the caller's
          // charge that follows is theirs to bound, not ours to time out
          // with a "did not respond" over a payment in flight.
          activeSubmitRef.current = null;
          if (submitTimeoutRef.current !== null) {
            window.clearTimeout(submitTimeoutRef.current);
            submitTimeoutRef.current = null;
          }
          const normalized = extractResult(result, rail);
          if (!normalized) {
            finishSubmitting();
            setErrorMessage(
              result.responseText ??
                result.resultText ??
                'Payabli did not return a saved payment method.',
            );
            return;
          }
          void onCompleteRef
            .current(normalized)
            .catch((error: unknown) => {
              if (!isCurrent()) return;
              setErrorMessage(
                error instanceof Error ? error.message : 'Failed to save payment method.',
              );
            })
            .finally(() => {
              if (isCurrent()) finishSubmitting();
            });
        },
        functionCallBackError: (error) => {
          if (!ownsActiveSubmit()) return;
          finishSubmitting();
          // Customers see this form, so the message is plain words about
          // what to fix, never Payabli's wording or codes (paypoints, tokens,
          // `customerError`). Support gets the keys and codes in the console,
          // not the payload: that can be Payabli's whole response.
          console.warn('Payment method refused', { rail, ...payabliRefusalSummary(error) });
          setErrorMessage(payabliRefusalMessage(error, rail));
        },
      };
    },
  });
  const { instanceRef, instanceTokenRef, initializedInstanceRef } = embed;
  const { isRendered, isInitialized, loadError } = embed;
  const hideFrame = isCustomer && !isInitialized && !loadError;

  const submitText = typeof submitLabel === 'function' ? submitLabel(rail) : submitLabel;

  function selectRail(next: PayabliRail) {
    if (next === rail || isSubmitting) return;
    setErrorMessage(null);
    // The effect above reports it upward once the new rail is the effective
    // one — reporting here too would announce a rail the form has not
    // switched to yet.
    setSelectedRail(next);
  }

  function handleSubmit() {
    if (
      !instanceRef.current ||
      !instanceTokenRef.current ||
      initializedInstanceRef.current !== instanceTokenRef.current ||
      activeSubmitRef.current
    )
      return;
    setIsSubmitting(true);
    setErrorMessage(null);
    if (submitTimeoutRef.current !== null) window.clearTimeout(submitTimeoutRef.current);
    activeSubmitRef.current = instanceTokenRef.current;
    submitTimeoutRef.current = window.setTimeout(() => {
      submitTimeoutRef.current = null;
      // Abandon the attempt as well as the spinner: a token that arrives after
      // this must not be charged behind the payer's back.
      activeSubmitRef.current = null;
      setIsSubmitting(false);
      setErrorMessage(
        'The payment form did not respond. Check your details and try again, or reload the page.',
      );
    }, SUBMIT_TIMEOUT_MS);
    // 'method' = tokenize the payment method without charging.
    instanceRef.current.payabliExec('method');
  }

  return (
    <div className={isCustomer ? 'space-y-5' : 'space-y-4'}>
      {bothRails && isCustomer ? (
        <PaymentRailSwitch
          value={rail}
          onChange={selectRail}
          disabled={isSubmitting}
          achSavings={achSavings}
          testIdPrefix="payabli-rail"
        />
      ) : bothRails ? (
        <div
          role="radiogroup"
          aria-label="Payment method"
          className="grid grid-cols-2 gap-2"
          data-testid="payabli-rail-switch"
        >
          {(
            [
              { value: 'card', label: 'Card', Icon: CreditCard },
              { value: 'ach', label: 'Bank account', Icon: Building2 },
            ] as const
          ).map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={rail === value}
              disabled={isSubmitting}
              onClick={() => selectRail(value)}
              data-testid={`payabli-rail-${value}`}
              className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 ${
                rail === value
                  ? 'border-primary bg-primary/5 text-foreground'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex min-w-0 flex-col items-center gap-1.5">
                <span className="flex items-center gap-2">
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                </span>{' '}
                {value === 'ach' ? achSavings : null}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* The customer stylesheet draws each field's own border, so no frame. */}
      <div className={isCustomer ? 'relative -m-1' : 'rounded-lg border border-border p-4'}>
        <div
          id={embed.containerId}
          // Payabli shows its own white loading page, then its unstyled fields,
          // until our stylesheet lands inside the iframe. On a customer page the
          // iframe stays hidden until it is ready to take the payment (the same
          // signal that enables the button), or until it reports a problem.
          className={
            hideFrame ? 'pointer-events-none absolute inset-x-0 top-0 opacity-0' : undefined
          }
          aria-hidden={hideFrame || undefined}
        />
        {hideFrame ? (
          <PayabliFieldsPlaceholder rail={rail} />
        ) : !isRendered && !loadError ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : null}
      </div>

      {loadError ? (
        <Alert variant="destructive" data-testid="payabli-load-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Payment form unavailable</AlertTitle>
          <AlertDescription>
            <p>{loadError}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              data-testid="payabli-retry"
              onClick={embed.retry}
            >
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {errorMessage ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{errorTitle}</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : null}

      {beforeSubmit}

      {isCustomer ? (
        <CustomerPayButton
          label={submitText}
          busy={isSubmitting}
          disabled={!isInitialized || disabled}
          onClick={handleSubmit}
        />
      ) : (
        <Button
          type="button"
          className="w-full"
          disabled={!isInitialized || isSubmitting || disabled}
          onClick={handleSubmit}
        >
          {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {submitText}
        </Button>
      )}
    </div>
  );
}
